import { createContext, useState, useEffect, useMemo, useCallback, useRef } from "react";
import PropTypes from "prop-types";

const CalculatorContext = createContext();

export function CalculatorProvider({ children }) {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  // STEP 1
  const [forecastStartYear, setForecastStartYear] = useState("");

  // STEP 2
  const [energySource, setEnergySource] = useState("");

  // STEP 3
  const [isEmissionKnown, setIsEmissionKnown] = useState("");

  // STEP 4
  const [customEmission, setCustomEmission] = useState("");
  const [fuelConsumptionAmount, setFuelConsumptionAmount] = useState("");
  const [fuelConsumptionType, setFuelConsumptionType] = useState("kWh");
  const [useDefaultFernwarmeFactor, setUseDefaultFernwarmeFactor] = useState(true);
  const [customFernwarmeFactor, setCustomFernwarmeFactor] = useState("");

  // STEP 5
  const [livingSpace, setLivingSpace] = useState("");

  // STEP 6
  const [isCommercialProperty, setIsCommercialProperty] = useState("");

  // STEP 7
  const [isEnergyImprovementPossible, setIsEnergyImprovementPossible] = useState("");

  // STEP 8
  const [isSupplyImprovementPossible, setIsSupplyImprovementPossible] = useState("");

  // STEP 9
  const [co2Prices, setCo2Prices] = useState({});

  const createDefaultCo2Prices = useCallback((startYear) => {
    const year = Number(startYear);
    const initPrices =
      year === 2025
        ? [55, 60, 65, 75, 90, 120, 140, 170, 200, 230]
        : [60, 65, 75, 90, 120, 140, 170, 200, 230, 250];

    return Object.fromEntries(initPrices.map((price, i) => [year + i, price]));
  }, []);

  const resetCo2Prices = useCallback(
    () => setCo2Prices(createDefaultCo2Prices(forecastStartYear)),
    [createDefaultCo2Prices, forecastStartYear, setCo2Prices],
  );

  const prevYearRef = useRef();

  useEffect(() => {
    if (!forecastStartYear) return;
    if (prevYearRef.current === forecastStartYear) return;

    prevYearRef.current = forecastStartYear;

    setCo2Prices((prevPrices) => {
      const defaultMap = createDefaultCo2Prices(Number(forecastStartYear));

      // If empty → just use defaults
      if (Object.keys(prevPrices).length === 0) {
        return defaultMap;
      }

      // Merge only existing keys
      const merged = { ...defaultMap };

      Object.keys(prevPrices).forEach((k) => {
        if (k in merged) {
          merged[k] = prevPrices[k];
        }
      });

      return merged;
    });
  }, [forecastStartYear, createDefaultCo2Prices, setCo2Prices]);

  // SIMULATION
  const [isSimulationEnabled, setIsSimulationEnabled] = useState("");

  useEffect(() => {
    if (!fuelConsumptionAmount) {
      return;
    }

    let fuelConsumptionAmountFactor = 0;

    switch (energySource) {
      case "Erdgas":
        if (fuelConsumptionType === "kWh") {
          fuelConsumptionAmountFactor = 10;
        } else {
          fuelConsumptionAmountFactor = 1 / 10;
        }
        break;
      // case "Flüssiggas":
      //   if (fuelConsumptionType === "kWh") {
      //     fuelConsumptionAmountFactor = 6570;
      //   } else {
      //     fuelConsumptionAmountFactor = 1 / 6570;
      //   }
      //   break;
      // case "Heizöl":
      //   if (fuelConsumptionType === "kWh") {
      //     fuelConsumptionAmountFactor = 9800;
      //   } else {
      //     fuelConsumptionAmountFactor = 1 / 9800;
      //   }
      //   break;
      case "Flüssiggas":
        if (fuelConsumptionType === "kWh") {
          fuelConsumptionAmountFactor = 6.57;
        } else {
          fuelConsumptionAmountFactor = 1 / 6.57;
        }
        break;
      case "Heizöl":
        if (fuelConsumptionType === "kWh") {
          fuelConsumptionAmountFactor = 9.8;
        } else {
          fuelConsumptionAmountFactor = 1 / 9.8;
        }
        break;
      case "Fernwärme":
        if (fuelConsumptionType === "kWh") {
          fuelConsumptionAmountFactor = 10;
        } else {
          fuelConsumptionAmountFactor = 1 / 10;
        }
        break;
      default:
        setFuelConsumptionAmount("");
    }

    setFuelConsumptionAmount(+(fuelConsumptionAmount * fuelConsumptionAmountFactor).toFixed(6));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [energySource, fuelConsumptionType, setFuelConsumptionAmount]);

  // to prevent miscalculation on going back to step one and again on step two
  useEffect(() => {
    setFuelConsumptionAmount("");
    setFuelConsumptionType("kWh");

    setUseDefaultFernwarmeFactor(true);
    setCustomFernwarmeFactor("");
  }, [
    energySource,
    setFuelConsumptionAmount,
    setFuelConsumptionType,
    setUseDefaultFernwarmeFactor,
    setCustomFernwarmeFactor,
  ]);

  const value = useMemo(
    () => ({
      currentStepIndex,
      setCurrentStepIndex,
      forecastStartYear,
      setForecastStartYear,
      energySource,
      setEnergySource,
      isEmissionKnown,
      setIsEmissionKnown,
      customEmission,
      setCustomEmission,
      fuelConsumptionAmount,
      setFuelConsumptionAmount,
      fuelConsumptionType,
      setFuelConsumptionType,
      useDefaultFernwarmeFactor,
      setUseDefaultFernwarmeFactor,
      customFernwarmeFactor,
      setCustomFernwarmeFactor,
      livingSpace,
      setLivingSpace,
      isCommercialProperty,
      setIsCommercialProperty,
      isEnergyImprovementPossible,
      setIsEnergyImprovementPossible,
      isSupplyImprovementPossible,
      setIsSupplyImprovementPossible,
      createDefaultCo2Prices,
      resetCo2Prices,
      co2Prices,
      setCo2Prices,
      isSimulationEnabled,
      setIsSimulationEnabled,
    }),
    [
      currentStepIndex,
      forecastStartYear,
      resetCo2Prices,
      createDefaultCo2Prices,
      energySource,
      isEmissionKnown,
      customEmission,
      fuelConsumptionAmount,
      fuelConsumptionType,
      useDefaultFernwarmeFactor,
      customFernwarmeFactor,
      livingSpace,
      isCommercialProperty,
      isEnergyImprovementPossible,
      isSupplyImprovementPossible,
      co2Prices,
      isSimulationEnabled,
    ],
  );

  return <CalculatorContext.Provider value={value}>{children}</CalculatorContext.Provider>;
}

CalculatorProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default CalculatorContext;
